/**
 * PrimaryPageNotice
 *
 * Visas högst upp på stödsidor i ett kluster (stadssidor och prisguider som
 * pekar canonical mot en annan URL). Ger besökaren en direkt väg till den
 * primära sidan och skickar länkstyrkan vidare internt.
 */

import { Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { isConsolidated, primaryPathFor } from "@/lib/seo/canonical-overrides";
import { CITIES } from "@/lib/seo/cities";
import { trackInternalLink } from "@/lib/analytics";

const normalize = (p: string) => (p.length > 1 ? p.replace(/\/+$/, "") : "/");

const CITY_NAME = new Map(CITIES.map((c) => [c.slug, c.name]));

function labelFor(primary: string): string | null {
  const city = primary.match(/^\/filmproduktion\/([a-z0-9-]+)$/);
  if (city) {
    const name = CITY_NAME.get(city[1]!) ?? city[1]!;
    return `Se vår huvudsida för film i ${name}`;
  }
  const price = primary.match(/^\/fragor\/vad-kostar-(.+)$/);
  if (price) {
    const service = price[1]!.replace(/-/g, " ");
    return `Full prisguide: vad kostar ${service}`;
  }
  return "Se vår huvudsida för det här ämnet";
}

export function PrimaryPageNotice() {
  const pathname = useRouterState({ select: (s) => normalize(s.location.pathname) });
  if (!isConsolidated(pathname)) return null;

  const primary = primaryPathFor(pathname);
  const label = labelFor(primary);
  if (!label) return null;

  return (
    <div className="border-b border-foreground/10 bg-foreground/[0.03]">
      <div className="mx-auto w-full max-w-6xl px-5 py-3 sm:px-8">
        <Link
          to={primary}
          onClick={() =>
            trackInternalLink("related_link_click", {
              location: "primary_page_notice",
              label,
              to: primary,
              from: pathname,
            })
          }
          className="group inline-flex items-center gap-2 text-sm text-foreground/80 transition hover:text-foreground"
        >
          <span>{label}</span>
          <ArrowRight
            size={14}
            className="shrink-0 transition group-hover:translate-x-0.5"
          />
        </Link>
      </div>
    </div>
  );
}
